/** @format */
import { inject, Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import { dateUtils } from 'common-typescript/constants';
import { OtmId, StudyRight, StudyYear } from 'common-typescript/types';
import moment from 'moment';
import { combineLatest, Observable, of, shareReplay } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { StudyYearsEntityService, UniversityService } from 'sis-components';
import { SelectedValidatablePlanService } from '../service/selected-validatable-plan.service';
import { ParsedPlannedPeriod, ParsedPlannedPeriodService } from './parsed-planned-period.service';
import { SelectedPlanStudyRightService } from './selected-plan-study-right.service';
import { TimelineStartDateService } from './timeline-start-date.service';
import { STUDY_TERMS_VISIBLE_AFTER_STUDY_RIGHT, TimelineTrimService } from './timeline-trim.service';

// In addition to the current year, fetch 9 extra years, if no study right end date.
const EXTRA_YEARS_TO_FETCH_AFTER_CURRENT_YEAR: number = 9;
// Including the study right ending year, fetch one study year per shown term (year has at least 1 term) to show STUDY_TERMS_VISIBLE_AFTER_STUDY_RIGHT terms ahead.
const EXTRA_YEARS_TO_FETCH_AFTER_STUDY_RIGHT_ENDING_YEAR: number = STUDY_TERMS_VISIBLE_AFTER_STUDY_RIGHT - 1;

@Injectable()
export class TimelineStudyYearsService {
    private readonly studyYearsEntityService = inject(StudyYearsEntityService);
    private readonly selectedValidatablePlanService = inject(SelectedValidatablePlanService);
    private readonly universityService = inject(UniversityService);
    private readonly selectedPlanStudyRightService = inject(SelectedPlanStudyRightService);
    private readonly parsedPlannedPeriodService: ParsedPlannedPeriodService = inject(ParsedPlannedPeriodService);
    private readonly timelineStartDateService: TimelineStartDateService = inject(TimelineStartDateService);
    private readonly timelineTrimService: TimelineTrimService = inject(TimelineTrimService);

    /**
     * Emits study years that are visible on the timeline, that is, full and partial study years containing interesting events
     * (curriculum period, study right, attainments, timings) and also some extra in the end.
     * If there is no {@link SelectedValidatablePlanService.selectedValidatablePlan$}, an empty array is returned.
     */
    readonly studyYears$: Observable<readonly StudyYear[]> = this.getStudyYearsForSelectedPlan();

    private getStudyYearsForSelectedPlan(): Observable<readonly StudyYear[]> {
        return combineLatest([
            this.selectedValidatablePlanService.selectedValidatablePlan$,
            this.selectedPlanStudyRightService.studyRight$,
        ]).pipe(
            switchMap(([validatablePlan, studyRight]: [ValidatablePlan | null, StudyRight | null]) => {
                // Without a selected plan there's no point in running (not that simple) fallback logic to show study years.
                // This should be a rare corner case, where we don't really want to show anything, but tell the user to create a plan.
                if (!validatablePlan) {
                    return of([]);
                }

                const sortedParsedPlannedPeriods: readonly ParsedPlannedPeriod[] =
                    this.parsedPlannedPeriodService.getSortedParsedPlannedPeriods(validatablePlan);
                const lastPlannedPeriod: ParsedPlannedPeriod | null = sortedParsedPlannedPeriods.length
                    ? sortedParsedPlannedPeriods[sortedParsedPlannedPeriods.length - 1]
                    : null;

                return this.timelineStartDateService.getTimelineStartDate(validatablePlan, studyRight).pipe(
                    switchMap(timelineStartDate => {
                        const universityOrgId: OtmId = this.universityService.getCurrentUniversityOrgId();

                        // timelineStartDate may belong to the study year starting in the previous calendar year,
                        // so let's fetch that one too just in case and get it filtered by TimelineTrimService if necessary.
                        const firstStudyYearToFetch: number = timelineStartDate.year() - 1;

                        const studyRightEndDate: moment.Moment | null = dateUtils.createMoment(studyRight?.valid?.endDate);
                        const inclusiveTimelineEndYearBasedOnStudyRight: number =
                            getInclusiveTimelineEndYearBasedOnStudyRight(studyRightEndDate);
                        const lastStudyYearToFetch: number = lastPlannedPeriod
                            ? Math.max(inclusiveTimelineEndYearBasedOnStudyRight, lastPlannedPeriod.parsedLocator.year)
                            : inclusiveTimelineEndYearBasedOnStudyRight;

                        return this.studyYearsEntityService
                            .getStudyYears(universityOrgId, firstStudyYearToFetch, lastStudyYearToFetch - firstStudyYearToFetch + 1)
                            .pipe(
                                map(studyYears =>
                                    this.timelineTrimService.trimTimeline(
                                        studyYears,
                                        timelineStartDate,
                                        studyRightEndDate,
                                        inclusiveTimelineEndYearBasedOnStudyRight,
                                        lastPlannedPeriod?.locator ?? null,
                                    ),
                                ),
                            );
                    }),
                );
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }
}

function getInclusiveTimelineEndYearBasedOnStudyRight(lastStudyRightEndDate: moment.Moment | null): number {
    return lastStudyRightEndDate
        ? lastStudyRightEndDate.year() + EXTRA_YEARS_TO_FETCH_AFTER_STUDY_RIGHT_ENDING_YEAR
        : moment().year() + EXTRA_YEARS_TO_FETCH_AFTER_CURRENT_YEAR;
}
